import { Building2, Calendar, User } from "lucide-react";
import type { Communications } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderSubtitleSegment } from "../model";
import { formatHeaderDate } from "../model";

/** Guarded against GetCommunications by detail-header-model-coverage.test.ts. */
export const COMMUNICATION_HEADER_FIELDS = [
  "type_label",
  "direction",
  "occurred_at",
  "contact_name",
  "company_id",
  "company_name",
] as const satisfies readonly (keyof Communications)[];

export type CommunicationHeaderData = Pick<
  Communications,
  (typeof COMMUNICATION_HEADER_FIELDS)[number]
>;

/** 1:1 scalar columns edited in the header (dropped from the inline panel). */
export const COMMUNICATION_HEADER_EDITABLE_FIELDS = [
  "occurred_at",
] as const satisfies readonly (keyof Communications)[];
type CommunicationHeaderEditableField = (typeof COMMUNICATION_HEADER_EDITABLE_FIELDS)[number];

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function buildCommunicationHeaderModel(
  communication: CommunicationHeaderData
): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (communication.type_label) subtitleSegments.push({ text: communication.type_label });
  if (communication.direction) {
    subtitleSegments.push({ text: capitalize(communication.direction) });
  }

  const facts: HeaderFact[] = [];
  if (communication.contact_name) {
    facts.push({ id: "contact", icon: User, value: communication.contact_name });
  }
  if (communication.company_name) {
    facts.push({
      id: "company",
      icon: Building2,
      value: communication.company_name,
      kind: communication.company_id ? "internal" : "text",
      href: communication.company_id ? `/companies/${communication.company_id}` : undefined,
    });
  }
  const occurredAt = formatHeaderDate(communication.occurred_at);
  if (occurredAt) {
    facts.push({
      id: "occurred",
      icon: Calendar,
      label: "Occurred",
      value: occurredAt,
      fieldId: "occurred_at" satisfies CommunicationHeaderEditableField,
    });
  }

  return { subtitleSegments, facts, metrics: [] };
}
